import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  ZAI_CHAT_FILE_COUNT_REFETCH_EVENT,
  ZAI_CHAT_MODAL_INFO_OPEN_EVENT,
  ZAI_CHAT_MODAL_UPLOAD_OPEN_EVENT,
} from '@winnin/zai-chat'

import { analyticsTracker } from 'utils'

import { ZaiChatObserverContext } from './context'
import type { IZaiChatObserverContextData, IZaiChatObserverProviderProps, TZaiChatInboundAction } from './types'

export function ZaiChatObserverProvider({ children }: IZaiChatObserverProviderProps) {
  const [modalCallbacks, setModalCallbacks] = useState<{
    onModalDocumentsOpen?: () => void
    onModalDocumentsTipsOpen?: () => void
  }>({})

  const registerModalCallbacks = useCallback(
    (callbacks: { onModalDocumentsOpen?: () => void; onModalDocumentsTipsOpen?: () => void }) => {
      setModalCallbacks(callbacks)
    },
    []
  )

  const onModalDocumentsOpenWithTracking = useCallback(() => {
    if (!modalCallbacks.onModalDocumentsOpen) return

    const { FIL_OPEN_MODAL } = analyticsTracker.eventNames

    analyticsTracker.eventTrack(FIL_OPEN_MODAL)

    modalCallbacks.onModalDocumentsOpen()
  }, [modalCallbacks])

  const actions: { [key in TZaiChatInboundAction]: () => void } = useMemo(
    () => ({
      'modal-upload-open': onModalDocumentsOpenWithTracking || (() => {}),
      'modal-info-open': modalCallbacks.onModalDocumentsTipsOpen || (() => {}),
    }),
    [onModalDocumentsOpenWithTracking, modalCallbacks.onModalDocumentsTipsOpen]
  )

  const triggerFileCountRefetch = useCallback(() => {
    window.dispatchEvent(new CustomEvent(ZAI_CHAT_FILE_COUNT_REFETCH_EVENT))
  }, [])

  useEffect(() => {
    window.addEventListener(ZAI_CHAT_MODAL_UPLOAD_OPEN_EVENT, actions['modal-upload-open'])
    window.addEventListener(ZAI_CHAT_MODAL_INFO_OPEN_EVENT, actions['modal-info-open'])

    return () => {
      window.removeEventListener(ZAI_CHAT_MODAL_UPLOAD_OPEN_EVENT, actions['modal-upload-open'])
      window.removeEventListener(ZAI_CHAT_MODAL_INFO_OPEN_EVENT, actions['modal-info-open'])
    }
  }, [actions])

  const contextValue: IZaiChatObserverContextData = useMemo(
    () => ({
      triggerFileCountRefetch,
      registerModalCallbacks,
    }),
    [triggerFileCountRefetch, registerModalCallbacks]
  )

  return <ZaiChatObserverContext.Provider value={contextValue}>{children}</ZaiChatObserverContext.Provider>
}
